"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const ZOOM_STEP = 0.35;

interface GalleryZoomModalProps {
  src: string | null;
  onClose: () => void;
}

/**
 * Fullscreen lightbox: opens fit-to-viewport (scale=1), then wheel/pinch/buttons
 * zoom with pan when magnified. Close via X, backdrop, or Escape.
 */
export default function GalleryZoomModal({ src, onClose }: GalleryZoomModalProps) {
  const [mounted, setMounted] = useState(false);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const scaleRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });
  const dragging = useRef(false);
  const lastPointer = useRef({ x: 0, y: 0 });
  const pinchStartDist = useRef(0);
  const pinchStartScale = useRef(1);
  const lastTap = useRef(0);
  const viewportRef = useRef<HTMLDivElement>(null);

  const syncTransform = useCallback((nextScale: number, nextOffset: { x: number; y: number }) => {
    const clamped = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
    const ox = clamped <= 1 ? 0 : nextOffset.x;
    const oy = clamped <= 1 ? 0 : nextOffset.y;
    scaleRef.current = clamped;
    offsetRef.current = { x: ox, y: oy };
    setScale(clamped);
    setOffset({ x: ox, y: oy });
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Reset when a new image opens
  useEffect(() => {
    if (!src) return;
    syncTransform(1, { x: 0, y: 0 });
  }, [src, syncTransform]);

  // Body scroll lock + Escape
  useEffect(() => {
    if (!src) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [src, onClose]);

  // Wheel zoom (center) — non-passive so we can prevent page scroll
  useEffect(() => {
    if (!src) return;
    const el = viewportRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const direction = e.deltaY < 0 ? 1 : -1;
      const next = scaleRef.current + direction * ZOOM_STEP * 0.6;
      syncTransform(next, offsetRef.current);
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [src, syncTransform]);

  if (!mounted || !src) return null;

  const zoomBy = (delta: number) => {
    syncTransform(scaleRef.current + delta, offsetRef.current);
  };

  const toggleZoomAt = () => {
    if (scaleRef.current > 1.05) {
      syncTransform(1, { x: 0, y: 0 });
    } else {
      syncTransform(2, offsetRef.current);
    }
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    // Double-click / double-tap toggle
    const now = Date.now();
    if (now - lastTap.current < 300) {
      lastTap.current = 0;
      toggleZoomAt();
      return;
    }
    lastTap.current = now;

    if (scaleRef.current <= 1) return;
    dragging.current = true;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current || scaleRef.current <= 1) return;
    const dx = e.clientX - lastPointer.current.x;
    const dy = e.clientY - lastPointer.current.y;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    syncTransform(scaleRef.current, {
      x: offsetRef.current.x + dx,
      y: offsetRef.current.y + dy,
    });
  };

  const onPointerUp = () => {
    dragging.current = false;
  };

  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      dragging.current = false;
      const [a, b] = [e.touches[0], e.touches[1]];
      pinchStartDist.current = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      pinchStartScale.current = scaleRef.current;
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const [a, b] = [e.touches[0], e.touches[1]];
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      if (pinchStartDist.current > 0) {
        const ratio = dist / pinchStartDist.current;
        syncTransform(pinchStartScale.current * ratio, offsetRef.current);
      }
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10020] flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Zoomed image"
    >
      {/* Dedicated dimmed backdrop — click to close */}
      <div
        className="absolute inset-0 bg-black/90"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-20 flex items-center justify-between gap-3 p-3 pointer-events-none">
        <div className="pointer-events-auto flex items-center gap-1 rounded-lg bg-black/50 backdrop-blur-sm p-1">
          <button
            type="button"
            onClick={() => zoomBy(-ZOOM_STEP)}
            disabled={scale <= MIN_SCALE}
            className="p-2.5 text-white rounded-md hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Zoom out"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          <span className="min-w-[3.5rem] text-center text-sm text-white/90 tabular-nums">
            {Math.round(scale * 100)}%
          </span>
          <button
            type="button"
            onClick={() => zoomBy(ZOOM_STEP)}
            disabled={scale >= MAX_SCALE}
            className="p-2.5 text-white rounded-md hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Zoom in"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="pointer-events-auto flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-black/10 backdrop-blur-sm text-white transition-all duration-200 opacity-80 hover:bg-black/70"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
          <span>Close</span>
        </button>
      </div>

      {/* Image layer above backdrop */}
      <div
        ref={viewportRef}
        className="relative z-10 flex-1 min-h-0 touch-none overflow-hidden pointer-events-none"
      >
        <div className="absolute inset-0 flex items-center justify-center p-3 md:p-6 pointer-events-none">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={src}
            alt="Zoomed result"
            draggable={false}
            className="max-w-full max-h-full object-contain select-none pointer-events-auto"
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              transformOrigin: "center center",
              cursor: scale > 1 ? (dragging.current ? "grabbing" : "grab") : "zoom-in",
              transition: dragging.current ? "none" : "transform 0.12s ease-out",
              willChange: "transform",
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>

      <p className="absolute bottom-3 inset-x-0 z-20 text-center text-xs text-white/50 pointer-events-none px-4">
        Scroll or pinch to zoom · drag when zoomed · Esc to close
      </p>
    </div>,
    document.body
  );
}
